import React from 'react';
import PropTypes from 'prop-types';
import {
  ToolOutlined,
  UserOutlined,
  ReloadOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';

import Colors from '../../../../../constants/Colors';
import BorderlessButton from '../../../../../common/buttons/BorderlessButton';

export default function RedistributeRowHeader({
  rowName,
  projectLabel,
  canReset,
  onReset,
  isEquipment,
  activeRowIndex,
  relevantRows,
  goToRow,
}) {
  const canGoPrevious = activeRowIndex > 0;
  const canGoNext = activeRowIndex < relevantRows.length - 1;

  const onPrevious = () => {
    const relevantRow = relevantRows[activeRowIndex - 1];
    if (relevantRow) goToRow(relevantRow.id);
  };

  const onNext = () => {
    const relevantRow = relevantRows[activeRowIndex + 1];
    if (relevantRow) goToRow(relevantRow.id);
  };

  const pageLabel = relevantRows.length
    ? `${activeRowIndex + 1} of ${relevantRows.length}`
    : '0 of 0';

  const iconContainerClassName = isEquipment
    ? 'ttg-redistribute-row-header-icon-container-equipment'
    : 'ttg-redistribute-row-header-icon-container-user';

  return (
    <div className="ttg-redistribute-row-header">
      <div className={iconContainerClassName}>
        {isEquipment ? (
          <ToolOutlined style={{ fontSize: 18, color: '#9810FA' }} />
        ) : (
          <UserOutlined style={{ fontSize: 18, color: Colors.BLUE_600 }} />
        )}
      </div>
      <div className="ttg-redistribute-row-header-info">
        <div className="ttg-redistribute-row-header-name">{rowName}</div>
        <div className="ttg-redistribute-row-header-breadcrumb">
          Distributing
          {' '}
          {isEquipment ? 'equipment' : 'unassigned'}
          {' '}
          hours
          {' - '}
          <span className="ttg-redistribute-row-header-project-label">{projectLabel}</span>
        </div>
      </div>

      <div className="ttg-redistribute-row-header-actions">
        <BorderlessButton
          className="ttg-redistribute-reset-button"
          iconNode={<ReloadOutlined style={{ fontSize: 18, color: Colors.GRAY_950 }} />}
          onClick={() => {
            if (!canReset) return;
            onReset();
          }}
        />

        <div className="ttg-redistribute-row-header-actions-divider" aria-hidden="true" />

        <div className="ttg-redistribute-pager">
          <BorderlessButton
            className={[
              'ttg-redistribute-pager-button',
              !canGoPrevious && 'ttg-redistribute-pager-button-disabled',
            ].filter(Boolean).join(' ')}
            iconNode={<LeftOutlined style={{ fontSize: 14, color: Colors.GRAY_950 }} />}
            onClick={() => {
              if (!canGoPrevious) return;
              onPrevious();
            }}
          />
          <span className="ttg-redistribute-pager-label">{pageLabel}</span>
          <BorderlessButton
            className={[
              'ttg-redistribute-pager-button',
              !canGoNext && 'ttg-redistribute-pager-button-disabled',
            ].filter(Boolean).join(' ')}
            iconNode={<RightOutlined style={{ fontSize: 14, color: Colors.GRAY_950 }} />}
            onClick={() => {
              if (!canGoNext) return;
              onNext();
            }}
          />
        </div>
      </div>
    </div>
  );
}

RedistributeRowHeader.propTypes = {
  rowName: PropTypes.string.isRequired,
  projectLabel: PropTypes.string.isRequired,
  canReset: PropTypes.bool.isRequired,
  isEquipment: PropTypes.bool.isRequired,
  activeRowIndex: PropTypes.number.isRequired,
  relevantRows: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })).isRequired,
  goToRow: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};
